import React, { Component } from 'react';
import PropTypes, { object } from 'prop-types';
import Modal from 'react-bootstrap/lib/Modal';
import classnames from 'classnames';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import Button from '@/shared/components/Button';
import EnumCacheService from '@/shared/services/EnumCacheService';
import { features, privileges, functionalities } from '@/shared/services/AuthService';
import AuthService from '@/shared/services/AuthService';

import Checkbox from '@/shared/components/input/Checkbox';
import intlShape from '@/shared/utils/intlShape';
import api from '@/shared/api';
import config from '@/config';
import handleHttpError from '@/shared/services/HandleHttpError';
import messages from './tenantRoleMessages';
import { CanUpdate } from '@/shared/components/security';

const emptyRole = { name: '', permissions: {} };

// eslint-disable-next-line no-bitwise
const hasPrivilege = (value, privilege) => (value & privilege) === privilege;
const isAdvanceWrite = value => {
  const writeCnt =
    (hasPrivilege(value, privileges.Create) ? 1 : 0) +
    (hasPrivilege(value, privileges.Update) ? 1 : 0) +
    (hasPrivilege(value, privileges.Delete) ? 1 : 0);

  return writeCnt !== 0 && writeCnt !== 3;
};

// eslint-disable-next-line no-bitwise
const writePrivilege = privileges.Create | privileges.Update | privileges.Delete;
const resetWritePermissions = permissions =>
  Object.keys(permissions).reduce((o, key) => {
    const value = permissions[key];
    o[key] = value;
    // eslint-disable-next-line no-bitwise
    if ((value & writePrivilege) !== writePrivilege) {
      // eslint-disable-next-line no-bitwise
      o[key] = value & ~writePrivilege;
    }
    return o;
  }, {});

class TenantRoleConfigModal extends Component {
  state = {
    advancedWrite: false,
    showValidation: false,
    saving: false,
    role: { ...emptyRole },
  };
  componentDidMount() {
    const { value, intl } = this.props;
    EnumCacheService.getTranslated('functionality', intl).then(translatedFunctionalities => {
      const role = { ...emptyRole, ...(value ?? {}) };
      const advancedWrite = Object.values(role.permissions).some(p => isAdvanceWrite(p));

      const hasTraining = AuthService.hasFeature(features.Training);
      const hasCapacityPlanning = AuthService.hasFeature(features.CapacityPlanning);
      const hasAI = AuthService.hasFeature(features.AI);
      const filteredFunctionalities = translatedFunctionalities.filter(f => {
        if (f.id === functionalities.TrainingManagement) {
          return hasTraining;
        }
        if (f.id === functionalities.CapacityPlanning) {
          return hasCapacityPlanning;
        }
        if (f.id === functionalities.AI) {
          return hasAI;
        }
        return true;
      });

      this.setState({
        role,
        functionalities: filteredFunctionalities,
        advancedWrite,
        showValidation: false,
        saving: false,
      });
    });
  }
  showValidation = () => this.setState({ showValidation: true });

  updateName = name => {
    this.setState(({ role }) => ({ role: { ...role, name } }));
  };

  updateFunctionality = (functionality, privilege, checked) => {
    this.setState(({ role }) => {
      let rights = role.permissions[functionality];
      if (checked) {
        // eslint-disable-next-line no-bitwise
        rights |= privilege;
      } else {
        // eslint-disable-next-line no-bitwise
        rights &= ~privilege;
      }

      return {
        role: { ...role, permissions: { ...role.permissions, [functionality]: rights } },
      };
    });
  };

  isValid = () => {
    const { role } = this.state;

    if (!role) return false;
    if (!role.name) return false;
    if (!role.permissions) return false;

    if (!Object.values(role.permissions).some(x => x !== 0)) return false;

    return true;
  };
  save = () => {
    const { saving, role } = this.state;
    const { onClose } = this.props;
    if (!this.isValid() || saving) return '';
    this.setState({ saving: true });

    const requestMethod = role.id ? api.base.put : api.base.post;

    return requestMethod(`${config.appUrls.api}/api/TenantRole`, role)
      .then(
        ({ data }) => {
          if (!data) return '';
          onClose(data);
          return data;
        },
        error => {
          const { intl } = this.props;
          handleHttpError(intl)(error);
          return error;
        },
      )
      .finally(() => {
        if (typeof this !== 'undefined') this.setState({ saving: false });
      });
  };
  toggleAdvanceWrite = value => {
    if (value) {
      this.setState({ advancedWrite: value });
    } else {
      this.setState(({ role }) => ({
        advancedWrite: value,
        role: { ...role, permissions: resetWritePermissions(role.permissions) },
      }));
    }
  };
  render() {
    const { role, saving, advancedWrite, functionalities, showValidation } = this.state;
    const { onClose, intl } = this.props;
    const isValid = this.isValid();
    return (
      <Modal backdrop="static" show onHide={() => onClose(null)} keyboard={false} bsSize="large">
        <Modal.Header closeButton>
          <Modal.Title>
            <FormattedMessage
              {...(role?.id ? messages.EditTenantRole : messages.CreateTenantRole)}
            />
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="form-horizontal">
            <div
              className={classnames('form-group', {
                'has-error': showValidation && !role.name,
              })}
            >
              <label className="control-label col-sm-4">
                <FormattedMessage {...messages.Name} />
              </label>
              <div className="col-sm-8">
                <input
                  type="text"
                  value={role.name}
                  maxLength={100}
                  required
                  onChange={e => this.updateName(e.target.value)}
                  className="form-control"
                  placeholder={intl.formatMessage(messages.Name)}
                  autoComplete="off"
                  disabled={saving}
                />
              </div>
            </div>
          </div>
          <table className="table">
            <thead>
              <tr>
                <th>
                  <FormattedMessage {...messages.Functionality} />
                </th>
                <th className="text-center">
                  <FormattedMessage {...messages.Read} />
                </th>
                <th className="text-center">
                  <FormattedMessage {...messages.Write} />
                </th>
                {advancedWrite && (
                  <>
                    <th className="text-center">
                      <FormattedMessage {...messages.Create} />
                    </th>
                    <th className="text-center">
                      <FormattedMessage {...messages.Update} />
                    </th>
                    <th className="text-center">
                      <FormattedMessage {...messages.Delete} />
                    </th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {functionalities?.map(f => {
                //
                const rights = role.permissions[f.id];
                return (
                  <tr key={f.id}>
                    <th>{f.label}</th>
                    <td className="text-center">
                      <Checkbox
                        onClick={value => this.updateFunctionality(f.id, privileges.Read, value)}
                        value={hasPrivilege(rights, privileges.Read)}
                      />
                    </td>
                    <td className="text-center">
                      <Checkbox
                        onClick={value => this.updateFunctionality(f.id, writePrivilege, value)}
                        value={hasPrivilege(rights, writePrivilege)}
                      />
                    </td>
                    {advancedWrite && (
                      <>
                        <td className="text-center">
                          <Checkbox
                            onClick={value =>
                              this.updateFunctionality(f.id, privileges.Create, value)
                            }
                            value={hasPrivilege(rights, privileges.Create)}
                          />
                        </td>
                        <td className="text-center">
                          <Checkbox
                            onClick={value =>
                              this.updateFunctionality(f.id, privileges.Update, value)
                            }
                            value={hasPrivilege(rights, privileges.Update)}
                          />
                        </td>
                        <td className="text-center">
                          <Checkbox
                            onClick={value =>
                              this.updateFunctionality(f.id, privileges.Delete, value)
                            }
                            value={hasPrivilege(rights, privileges.Delete)}
                          />
                        </td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Modal.Body>
        <Modal.Footer>
          <span className="pull-left">
            <Checkbox
              onClick={value => this.toggleAdvanceWrite(value)}
              value={advancedWrite}
              text={intl.formatMessage(messages.UseAdvanceWritePermissions)}
            />
          </span>
          <Button disabled={saving} onClick={() => onClose(null)}>
            <FormattedMessage {...messages.Cancel} />
          </Button>
          <CanUpdate on="CompanyOwn">
            <Button
              onClick={() => this.save()}
              disabled={showValidation && !isValid}
              onFocus={this.showValidation}
              onMouseOver={this.showValidation}
              btnClassName="btn-inverse"
              showProgress
            >
              <FormattedMessage {...(role?.id ? messages.Save : messages.Add)} />
            </Button>
          </CanUpdate>
        </Modal.Footer>
      </Modal>
    );
  }
}

TenantRoleConfigModal.propTypes = {
  intl: intlShape.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default injectIntl(TenantRoleConfigModal);
